/**
 * CIM-10 — Browser terminologie diagnostics WHO
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Library, Search, Star, TrendingUp, Activity, Brain, ArrowRight } from "lucide-react";
import { ICD10_ENTRIES, ICD10_CHAPTERS, type ICD10Entry } from "@/lib/referential/cim10";
import { useTerminologyRelease } from "@/lib/terminology-client";

export const Route = createFileRoute("/referential/cim10")({
  component: CIM10Browser,
});

function CIM10Browser() {
  const [search, setSearch] = useState("");
  const [filterChapter, setFilterChapter] = useState<string | "all">("all");
  const [showOnlyCommon, setShowOnlyCommon] = useState(false);
  const [selected, setSelected] = useState<ICD10Entry | null>(null);
  const prov = useTerminologyRelease("CIM10");

  const filtered = useMemo(() => {
    return ICD10_ENTRIES.filter((e) => {
      if (filterChapter !== "all" && e.chapterCode !== filterChapter) return false;
      if (showOnlyCommon && !e.isCommon) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return e.code.toLowerCase().includes(s) || e.label.toLowerCase().includes(s) || e.chapter.toLowerCase().includes(s);
    });
  }, [search, filterChapter, showOnlyCommon]);

  const top10Common = useMemo(
    () => [...ICD10_ENTRIES].filter((e) => e.isCommon).sort((a, b) => b.prevalence - a.prevalence).slice(0, 10),
    []
  );

  return (
    <div className="space-y-4 p-6">
      {{/*
        v10 Sprint 1 — provenance banner.
        Renders in ALL THREE states. A terminology outage must be visible;
        it must never look like a clean, certified page.
      */}}
      <div
        data-testid="terminology-provenance"
        data-status={prov.status}
        className={
          prov.status === "unavailable" || prov.status === "ok"
            ? "rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
            : "sr-only"
        }
      >
        {prov.status === "loading" && <span>Chargement de la source CIM-10…</span>}
        {prov.status === "unavailable" && (
          <><strong>CIM-10 :</strong> {prov.message}</>
        )}
        {prov.status === "ok" && prov.release?.isAuthoritative && (
          <><strong>CIM-10 :</strong> version certifiée {prov.release.version} ({prov.release.publisher ?? "source non précisée"})</>
        )}
        {prov.status === "ok" && !prov.release?.isAuthoritative && (
          <><strong>CIM-10 :</strong> {prov.warning}</>
        )}
      </div>
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Library className="h-5 w-5" /> CIM-10
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Classification Internationale des Maladies · {ICD10_ENTRIES.length} codes · {ICD10_CHAPTERS.length} chapitres
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Codes totaux</div>
            <div className="mt-1 text-3xl font-bold">{ICD10_ENTRIES.length}</div>
            <div className="text-xs text-muted-foreground">+14 400 dans la base OMS complète</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Codes courants Maroc</div>
            <div className="mt-1 text-3xl font-bold text-emerald-400">{ICD10_ENTRIES.filter((e) => e.isCommon).length}</div>
            <div className="text-xs text-muted-foreground">prévalence élevée (épidémio locale)</div>
          </CardContent>
        </Card>
      </div>

      {/* Top 10 common */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4" /> Top 10 des codes les plus fréquents (Maroc)
          </CardTitle>
          <CardDescription>Estimations de prévalence pour 100 000 habitants</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5">
            {top10Common.map((e) => (
              <li key={e.code} className="flex items-center gap-3 rounded-md border border-white/5 bg-white/[0.02] p-2 text-sm">
                <code className="font-mono font-bold w-16 shrink-0">{e.code}</code>
                <span className="flex-1">{e.label}</span>
                <div className="flex items-center gap-2 text-xs">
                  <div className="h-1.5 w-24 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${(e.prevalence / 4500) * 100}%` }} />
                  </div>
                  <span className="text-muted-foreground w-16 text-right font-mono">{e.prevalence}/100k</span>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (code, libellé, chapitre)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          size="sm"
          variant={showOnlyCommon ? "default" : "outline"}
          onClick={() => setShowOnlyCommon(!showOnlyCommon)}
        >
          <Star className="h-3.5 w-3.5 mr-1" />
          {showOnlyCommon ? "Tous" : "Fréquents uniquement"}
        </Button>
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        <Button
          size="sm"
          variant={filterChapter === "all" ? "default" : "outline"}
          onClick={() => setFilterChapter("all")}
        >
          Tous
        </Button>
        {ICD10_CHAPTERS.map((c) => (
          <Button
            key={c.code}
            size="sm"
            variant={filterChapter === c.code ? "default" : "outline"}
            onClick={() => setFilterChapter(c.code)}
            className="text-xs"
          >
            {c.code} · {c.name.length > 30 ? c.name.slice(0, 30) + "…" : c.name}
          </Button>
        ))}
      </div>

      {/* Results */}
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {filtered.map((e) => (
          <Card
            key={e.code}
            className={`cursor-pointer transition hover:border-primary/30 ${selected?.code === e.code ? "border-primary" : ""}`}
            onClick={() => setSelected(e)}
          >
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <code className="font-mono font-bold text-sm">{e.code}</code>
                {e.isCommon && <Star className="h-3 w-3 text-amber-400 fill-amber-400" />}
                <Badge variant="outline" className="text-[10px]">{e.chapterCode}</Badge>
              </div>
              <div className="mt-1 text-sm">{e.label}</div>
              <div className="mt-1 text-[10px] text-muted-foreground">{e.chapter}</div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="md:col-span-2 rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
            Aucun code ne correspond
          </div>
        )}
      </div>

      {/* Selected */}
      {selected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <code className="font-mono">{selected.code}</code> {selected.label}
            </CardTitle>
            <CardDescription>{selected.chapter} · chapitre {selected.chapterCode}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Prévalence</div>
                <div className="text-lg font-bold">{selected.prevalence}/100k</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Fréquence</div>
                <div className="text-lg font-bold">{selected.isCommon ? "★★★ Courant" : "★★ Peu fréquent"}</div>
              </div>
            </div>
            <Button asChild>
              <Link to="/consultation">
                Utiliser dans une consultation <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
